import React, { memo, useCallback } from "react";
import { Checkbox, Label, Radio, Select } from "flowbite-react";
import { Surface } from "../../../../atoms/Surface";
import { FieldLabel } from "../../../../atoms/FieldLabel";
import { BiPlus } from "react-icons/bi";
import { HiOutlineTrash } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { Control, Controller, useFieldArray, useWatch } from "react-hook-form";
import { QuestionFieldType, RegistrationFormData } from "../types";
import { CustomButton } from "../../../../atoms/CustomButton";
import { CustomInput } from "../../../../atoms/CustomInput";
import { CharacterCounter } from "../../../../atoms/CharacterCounter";
import parse from "html-react-parser";
import FeatureUpgradePrompt from "../../../../atoms/FeatureUpgradePrompt";
import { PlansModal } from "../../../../modals/PlansModal";
import { PlanTier, usePermissions } from "../../../../../utils/permissions";
import useAuth from "../../../../../hooks/useAuth";

type Props = {
  control: Control<RegistrationFormData>;
  errors: any;
  readOnly?: boolean;
};

const MAX_QUESTION_LENGTH = 300;

const QuestionInputWithCounter: React.FC<{
  questionIndex: number;
  control: Control<RegistrationFormData>;
  errors: any;
  t: any;
}> = ({ questionIndex, control, errors, t }) => {
  const questionValue = useWatch({
    control,
    name: `customQuestions.${questionIndex}.question`,
  });

  const currentLength = questionValue?.length || 0;

  return (
    <div className="relative" translate="no">
      <CustomInput
        name={`customQuestions.${questionIndex}.question`}
        control={control}
        label={t("customRegistration.customQuestions.questionText") as string}
        placeholder={
          t("customRegistration.customQuestions.questionPlaceholder") as string
        }
        error={errors?.customQuestions?.[questionIndex]?.question}
        helperText={errors?.customQuestions?.[questionIndex]?.question}
        rows={1}
        maxLength={MAX_QUESTION_LENGTH}
      />
      <CharacterCounter
        currentLength={currentLength}
        maxLength={MAX_QUESTION_LENGTH}
      />
    </div>
  );
};

const MAX_OPTIONS = 10;
const MAX_OPTION_LENGTH = 60;

const OptionInputWithCounter: React.FC<{
  questionIndex: number;
  optionIndex: number;
  control: Control<RegistrationFormData>;
  errors: any;
  onRemove: () => void;
  readOnly?: boolean;
  t: any;
}> = ({
  questionIndex,
  optionIndex,
  control,
  errors,
  onRemove,
  readOnly = false,
  t,
}) => {
  const optionValue = useWatch({
    control,
    name: `customQuestions.${questionIndex}.options.${optionIndex}` as any,
  });

  const currentLength = optionValue?.length || 0;

  return (
    <div className="flex gap-2 items-start">
      <div className="flex-1">
        <CustomInput
          name={
            `customQuestions.${questionIndex}.options.${optionIndex}` as any
          }
          control={control}
          placeholder={
            t("customRegistration.customQuestions.optionPlaceholder") as string
          }
          error={
            errors?.customQuestions?.[questionIndex]?.options?.[optionIndex]
          }
          maxLength={MAX_OPTION_LENGTH}
        />
        <CharacterCounter
          currentLength={currentLength}
          maxLength={MAX_OPTION_LENGTH}
        />
      </div>
      {!readOnly && (
        <CustomButton
          variant="none"
          icon={<HiOutlineTrash size={18} />}
          onClick={onRemove}
          title=""
          size="icon"
          tooltip={t("common.delete") as string}
          className="mt-1 rounded-md text-gray-400 transition-colors enabled:hover:text-red-600"
        />
      )}
    </div>
  );
};

const QuestionOptionsManager: React.FC<{
  questionIndex: number;
  control: Control<RegistrationFormData>;
  errors: any;
  readOnly?: boolean;
}> = ({ questionIndex, control, errors, readOnly = false }) => {
  const { t } = useTranslation();
  const fieldType = useWatch({
    control,
    name: `customQuestions.${questionIndex}.fieldType`,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: `customQuestions.${questionIndex}.options` as any,
  });

  const handleAddOption = useCallback(() => append("" as any), [append]);

  if (fieldType !== "radio" && fieldType !== "checkbox") {
    return null;
  }

  return (
    <div className="mt-4 p-3 bg-white rounded-md border border-gray-300">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
        <FieldLabel className="mb-0">
          {t("customRegistration.customQuestions.optionsLabel", {
            max: MAX_OPTIONS,
          })}
        </FieldLabel>
        {!readOnly && fields.length < MAX_OPTIONS && (
          <button
            type="button"
            onClick={handleAddOption}
            className="self-start sm:self-auto text-xs text-blue-600 hover:text-blue-700 font-medium"
          >
            {t("customRegistration.customQuestions.addOptionButton", {
              current: fields.length,
              max: MAX_OPTIONS,
            })}
          </button>
        )}
      </div>

      <div className="space-y-2">
        {fields.map((field, optIdx) => (
          <OptionInputWithCounter
            key={field.id}
            questionIndex={questionIndex}
            optionIndex={optIdx}
            control={control}
            errors={errors}
            onRemove={() => remove(optIdx)}
            readOnly={readOnly}
            t={t}
          />
        ))}

        {fields.length === 0 && (
          <div className="text-xs text-gray-500 italic py-2">
            {t("customRegistration.customQuestions.noOptionsYet")}
          </div>
        )}
      </div>

      {errors?.customQuestions?.[questionIndex]?.options?.message && (
        <p className="text-xs text-red-600 mt-2">
          {errors.customQuestions[questionIndex].options.message}
        </p>
      )}
    </div>
  );
};

export const CustomQuestionsSection: React.FC<Props> = memo(
  ({ control, errors, readOnly = false }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const permissions = usePermissions(user);
    const [showUpgradeModal, setShowUpgradeModal] = React.useState(false);
    const MAX_QUESTIONS = permissions.isPlanTier(PlanTier.FREE)
      ? 1
      : permissions.isAtLeastTier(PlanTier.ENTERPRISE)
      ? 15
      : 10;

    const { fields, append, remove } = useFieldArray({
      control,
      name: "customQuestions",
    });

    const handleAdd = useCallback(() => {
      append({ question: "", fieldType: "text", required: false });
    }, [append]);

    return (
      <Surface>
        <div className="mb-4">
          <p className="text-gray-500 text-sm leading-tight">
            {t("customRegistration.customQuestions.description", {
              count: MAX_QUESTIONS,
            })}
          </p>
        </div>

        {fields.length > 0 && (
          <div className="mb-6">
            <div className="mb-3">
              <h3 className="text-gray-700 text-sm font-semibold mb-1">
                {t("customRegistration.customQuestions.positionLabel")}
              </h3>
            </div>
            <Controller
              name="customQuestionsPosition"
              control={control}
              render={({ field }) => (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label
                    htmlFor="position-first"
                    className={`relative flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      field.value === "first"
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center h-5">
                      <Radio
                        id="position-first"
                        value="first"
                        checked={field.value === "first"}
                        onChange={(e) => field.onChange(e.target.value)}
                        className="focus:ring-blue-500"
                      />
                    </div>
                    <div className="ml-3 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`text-sm font-medium ${
                            field.value === "first"
                              ? "text-blue-900"
                              : "text-gray-700"
                          }`}
                        >
                          {parse(
                            t(
                              "customRegistration.customQuestions.positionFirst"
                            )
                          )}
                        </span>
                      </div>
                    </div>
                  </label>

                  <label
                    htmlFor="position-last"
                    className={`relative flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      field.value === "last"
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center h-5">
                      <Radio
                        id="position-last"
                        value="last"
                        checked={field.value === "last"}
                        onChange={(e) => field.onChange(e.target.value)}
                        className="focus:ring-blue-500"
                      />
                    </div>
                    <div className="ml-3 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`text-sm font-medium ${
                            field.value === "last"
                              ? "text-blue-900"
                              : "text-gray-700"
                          }`}
                        >
                          {parse(
                            t("customRegistration.customQuestions.positionLast")
                          )}
                        </span>
                      </div>
                    </div>
                  </label>
                </div>
              )}
            />
          </div>
        )}

        <div className="space-y-4">
          {fields.map((f, idx) => (
            <div
              key={f.id}
              className="p-4 bg-gray-50 rounded-md border border-gray-200"
            >
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-gray-700 text-sm font-semibold">
                  {t("customRegistration.customQuestions.questionLabel")}{" "}
                  {idx + 1}
                </h3>
                {!readOnly && (
                  <CustomButton
                    variant="none"
                    icon={<HiOutlineTrash size={18} />}
                    onClick={() => remove(idx)}
                    title=""
                    size="icon"
                    tooltip={t("common.delete") as string}
                    className="rounded-md text-gray-400 transition-colors enabled:hover:text-red-600"
                  />
                )}
              </div>

              <div className="space-y-3">
                <QuestionInputWithCounter
                  questionIndex={idx}
                  control={control}
                  errors={errors}
                  t={t}
                />

                <div>
                  <FieldLabel htmlFor={`fieldType-${idx}`}>
                    {t("customRegistration.customQuestions.fieldTypeLabel")}
                  </FieldLabel>
                  <Controller
                    name={`customQuestions.${idx}.fieldType`}
                    control={control}
                    render={({ field }) => (
                      <Select
                        id={`fieldType-${idx}`}
                        value={field.value || "text"}
                        onChange={(e) =>
                          field.onChange(e.target.value as QuestionFieldType)
                        }
                      >
                        <option value="text">
                          {t(
                            "customRegistration.customQuestions.fieldTypes.text"
                          )}
                        </option>
                        <option value="textarea">
                          {t(
                            "customRegistration.customQuestions.fieldTypes.textarea"
                          )}
                        </option>
                        <option value="number">
                          {t(
                            "customRegistration.customQuestions.fieldTypes.number"
                          )}
                        </option>
                        <option value="date">
                          {t(
                            "customRegistration.customQuestions.fieldTypes.date"
                          )}
                        </option>
                        <option value="datetime-local">
                          {t(
                            "customRegistration.customQuestions.fieldTypes.datetime"
                          )}
                        </option>
                        <option value="time">
                          {t(
                            "customRegistration.customQuestions.fieldTypes.time"
                          )}
                        </option>
                        <option value="file">
                          {t(
                            "customRegistration.customQuestions.fieldTypes.file"
                          )}
                        </option>
                        <option value="radio">
                          {t(
                            "customRegistration.customQuestions.fieldTypes.radio"
                          )}
                        </option>
                        <option value="checkbox">
                          {t(
                            "customRegistration.customQuestions.fieldTypes.checkbox"
                          )}
                        </option>
                      </Select>
                    )}
                  />
                </div>

                <div className="flex items-center">
                  <Controller
                    name={`customQuestions.${idx}.required`}
                    control={control}
                    render={({ field }) => (
                      <>
                        <div className="flex items-center h-5">
                          <Checkbox
                            id={`required-${idx}`}
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        </div>
                        <div className="ml-3">
                          <Label
                            htmlFor={`required-${idx}`}
                            className="text-gray-600 text-sm cursor-pointer"
                          >
                            {t(
                              "customRegistration.customQuestions.requiredField"
                            )}
                          </Label>
                        </div>
                      </>
                    )}
                  />
                </div>

                <QuestionOptionsManager
                  questionIndex={idx}
                  control={control}
                  errors={errors}
                  readOnly={readOnly}
                />
              </div>
            </div>
          ))}

          {!readOnly && fields.length < MAX_QUESTIONS && (
            <button
              type="button"
              onClick={handleAdd}
              className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 hover:border-blue-400 hover:bg-blue-50 transition-all group"
            >
              <div className="flex items-center justify-center gap-2">
                <BiPlus
                  size={20}
                  className="text-gray-400 group-hover:text-blue-500"
                />
                <span className="text-sm font-medium text-gray-600 group-hover:text-blue-600">
                  {t("customRegistration.customQuestions.addButton", {
                    current: fields.length,
                    max: MAX_QUESTIONS,
                  })}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {t("customRegistration.customQuestions.addDescription")}
              </p>
            </button>
          )}

          {!permissions.isAtLeastTier(PlanTier.ENTERPRISE) && (
            <FeatureUpgradePrompt
              featureName={
                t(
                  "permissions.customRegistration.customQuestions.title"
                ) as string
              }
              featureDescription={
                t(
                  "permissions.customRegistration.customQuestions.description"
                ) as string
              }
              benefits={[
                t(
                  "permissions.customRegistration.customQuestions.benefit1"
                ) as string,
                t(
                  "permissions.customRegistration.customQuestions.benefit2"
                ) as string,
                t(
                  "permissions.customRegistration.customQuestions.benefit3"
                ) as string,
              ]}
              onUpgrade={() => setShowUpgradeModal(true)}
              className="mb-0"
            />
          )}
        </div>

        <PlansModal
          showPlansModal={showUpgradeModal}
          setShowPlansModal={setShowUpgradeModal}
        />
      </Surface>
    );
  }
);
